// Castintech dashboard — Müşteriler (Customer Lifecycle) view modülü.
// Faz 21 PR-B — 7-state lifecycle kanban view.
// Faz 42 PR-E — Drill-down detail drawer (6 tab: Genel/Projeler/Faturalar/
//   Kararlar/İletişim/Aktivite). Tıklanabilir kart → sağdan açılan drawer
//   /customers/:slug/detail consolidated fetch. 30s TTL cache.
// Backend: GET /castintech-api/customer-lifecycle/all (Faz 21 PR-A — sibling).
// Backend (Faz 42 PR-B): GET /castintech-api/customers/:slug/detail.
// SSE: lifecycle.transition (Faz 18 PR-D), project.created/transition (Faz 42 PR-A).
//
// State erişimi: state.js direct import.
// Utils: utils.js (escHtml/escAttr/$).
// Api: apiFetch (api.js merkezi error boundary).
// Markdown: lib/md.js (renderMd) — Kararlar tab için.

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, $ } from '../utils.js';
import { renderMd } from '../lib/md.js';
import type {
  CustomerRow,
  CustomerDetail,
  CustomerProjectRow,
  CustomerProjectState,
  InvoiceTimelineRow,
  AuditRow,
  CustomerHistoryEntry,
} from '../../types/api';

const STATES = ['prospect', 'qualified', 'proposed', 'contracted', 'onboarding', 'active', 'churning', 'terminated'] as const;

const STATE_LABELS: Record<string, string> = {
  prospect: 'Aday',
  qualified: 'Nitelikli',
  proposed: 'Teklif Verildi',
  contracted: 'Sözleşmeli',
  onboarding: 'Onboarding',
  active: 'Aktif',
  churning: 'Kayıp Riski',
  terminated: 'Sonlandı',
};

// Faz 42 PR-A — 11-state proje state machine compact label set.
const PROJECT_STATES: CustomerProjectState[] = [
  'intake', 'discovery', 'proposal', 'accepted', 'planning',
  'execution', 'review', 'demo', 'launch', 'support', 'cancelled',
];

const PROJECT_STATE_LABELS: Record<string, string> = {
  intake: 'Talep',
  discovery: 'Keşif',
  proposal: 'Teklif',
  accepted: 'Kabul',
  planning: 'Plan',
  execution: 'Yapım',
  review: 'İnceleme',
  demo: 'Demo',
  launch: 'Yayın',
  support: 'Destek',
  cancelled: 'İptal',
};

const TAB_IDS = ['genel', 'projeler', 'faturalar', 'kararlar', 'iletisim', 'aktivite'] as const;
type TabId = (typeof TAB_IDS)[number];

const TAB_LABELS: Record<TabId, string> = {
  genel: 'Genel',
  projeler: 'Projeler',
  faturalar: 'Faturalar',
  kararlar: 'Kararlar',
  iletisim: 'İletişim',
  aktivite: 'Aktivite',
};

const DETAIL_CACHE_TTL_MS = 30_000;

interface CustomerLifecycleResponse {
  meta: { count: number; generated_at: string };
  customers: CustomerRow[];
}

export async function loadMusteriler(): Promise<void> {
  try {
    const r = await apiFetch('/castintech-api/customer-lifecycle/all');
    if (!r.ok) throw new Error('customer-lifecycle HTTP ' + r.status);
    const data = (await r.json()) as CustomerLifecycleResponse;
    state.musteriRows = Array.isArray(data.customers) ? data.customers : [];
    state.musteriMeta = data.meta || null;
    if (state.currentPage === 'musteriler') renderMusteriler();
  } catch (e) {
    console.warn('loadMusteriler failed:', (e as Error).message);
  }
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!t) return '—';
  const diff = Date.now() - t;
  if (diff < 0) return 'şimdi';
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'bugün';
  if (days === 1) return '1 gün';
  if (days < 7) return `${days} gün`;
  if (days < 30) return `${Math.floor(days / 7)} hafta`;
  return `${Math.floor(days / 30)} ay`;
}

function formatAmount(amount: string | number, currency: string): string {
  const n = typeof amount === 'number' ? amount : parseFloat(String(amount));
  if (!isFinite(n)) return `— ${currency}`;
  const formatted = n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sym = currency === 'TRY' ? '₺' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
  return `${formatted} ${sym}`;
}

export function renderMusteriler(): void {
  const root = $('musteriler-content');
  if (!root) return;

  const sub = $('musteriler-sub');
  if (sub) {
    const cnt = state.musteriRows ? state.musteriRows.length : 0;
    sub.textContent = state.musteriMeta
      ? `${cnt} müşteri · 7-state lifecycle kanban · kart aç → detail drawer`
      : 'yükleniyor...';
  }

  if (!state.musteriRows || state.musteriRows.length === 0) {
    root.innerHTML = '<div class="musteri-empty">📭 Müşteri bulunamadı.</div>';
    ensureDrawerDom();
    return;
  }

  // Group by current_state
  const grouped: Record<string, CustomerRow[]> = {};
  for (const s of STATES) grouped[s] = [];
  for (const c of state.musteriRows) {
    if (grouped[c.current_state]) grouped[c.current_state].push(c);
  }

  const html = STATES.map(s => {
    const rows = grouped[s];
    const cards = rows.map(c => `
      <div class="musteri-card" data-action="open-customer" data-slug="${escAttr(c.client_slug)}" role="button" tabindex="0">
        <div class="musteri-slug">${escHtml(c.client_slug)}</div>
        <div class="musteri-owner">@${escHtml(c.owner_agent || '—')}</div>
        <div class="musteri-meta">${escHtml(formatRelativeTime(c.updated_at || c.entered_at))}</div>
      </div>
    `).join('');
    return `
      <div class="musteri-column" data-state="${s}">
        <div class="musteri-column-header">
          <span>${escHtml(STATE_LABELS[s] || s)}</span>
          <span class="count">${rows.length}</span>
        </div>
        <div class="musteri-column-body">${cards || '<div class="musteri-col-empty">—</div>'}</div>
      </div>
    `;
  }).join('');

  root.innerHTML = `<div class="musteri-board">${html}</div>`;
  ensureDrawerDom();
  bindMusteriDelegationOnce();
  // Drawer açıksa state.musteriDrawerOpen → re-render (kart click sonrası
  // re-render edilmiş olabilir, drawer state korunsun).
  if (state.musteriDrawerOpen) {
    renderDrawer(state.musteriDrawerOpen);
  }
}

// ── Drawer DOM iskeleti ─────────────────────────────────────────────
// Tek sefer body'ye eklenir; render edilen DOM'a bağımsızdır.

function ensureDrawerDom(): void {
  if (document.getElementById('customer-drawer')) return;
  const drawer = document.createElement('aside');
  drawer.id = 'customer-drawer';
  drawer.className = 'drawer hidden';
  drawer.setAttribute('aria-hidden', 'true');
  drawer.innerHTML = `
    <header class="drawer-header">
      <h2 id="drawer-title">—</h2>
      <button id="drawer-close" type="button" aria-label="Kapat">×</button>
    </header>
    <nav class="tabs" id="drawer-tabs">
      ${TAB_IDS.map(t => `<button type="button" data-tab="${t}">${escHtml(TAB_LABELS[t])}</button>`).join('')}
    </nav>
    <section id="drawer-body" class="drawer-body">—</section>
  `;
  document.body.appendChild(drawer);
}

// ── Event delegation (idempotent — guard flag) ──────────────────────

let _delegationBound = false;
function bindMusteriDelegationOnce(): void {
  if (_delegationBound) return;
  _delegationBound = true;

  // Kart click → drawer aç. Faz 10 paterni: e.target.closest('[data-action]').
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLElement)) return;

    const card = t.closest<HTMLElement>('[data-action="open-customer"]');
    if (card) {
      const slug = card.getAttribute('data-slug') || '';
      if (slug) openDrawer(slug);
      return;
    }

    if (t.id === 'drawer-close' || t.closest('#drawer-close')) {
      closeDrawer();
      return;
    }

    const tabBtn = t.closest<HTMLElement>('#drawer-tabs button[data-tab]');
    if (tabBtn) {
      const tab = tabBtn.getAttribute('data-tab') as TabId | null;
      if (tab && TAB_IDS.includes(tab)) {
        state.musteriDrawerTab = tab;
        if (state.musteriDrawerOpen) renderDrawer(state.musteriDrawerOpen);
      }
    }
  });

  // Keyboard: Escape close, Enter on card open.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.musteriDrawerOpen) {
      closeDrawer();
    }
  });
}

// ── Drawer aç/kapat ─────────────────────────────────────────────────

export async function openDrawer(slug: string): Promise<void> {
  state.musteriDrawerOpen = slug;
  ensureDrawerDom();
  bindMusteriDelegationOnce();
  const drawer = $('customer-drawer');
  if (drawer) {
    drawer.classList.remove('hidden');
    drawer.setAttribute('aria-hidden', 'false');
  }
  // İlk anda render — cache varsa hemen görünür, yoksa loading state.
  renderDrawer(slug);
  // Cache kontrolü + fetch.
  const cached = state.musteriDetailCache[slug];
  const fresh = cached && (Date.now() - cached.fetchedAt) < DETAIL_CACHE_TTL_MS;
  if (!fresh) {
    await fetchCustomerDetail(slug);
  }
}

export function closeDrawer(): void {
  state.musteriDrawerOpen = null;
  const drawer = $('customer-drawer');
  if (drawer) {
    drawer.classList.add('hidden');
    drawer.setAttribute('aria-hidden', 'true');
  }
}

async function fetchCustomerDetail(slug: string): Promise<void> {
  try {
    const r = await apiFetch(`/castintech-api/customers/${encodeURIComponent(slug)}/detail`);
    if (!r.ok) throw new Error('customer detail HTTP ' + r.status);
    const data = (await r.json()) as CustomerDetail;
    state.musteriDetailCache[slug] = { data, fetchedAt: Date.now() };
    if (state.musteriDrawerOpen === slug) renderDrawer(slug);
  } catch (e) {
    console.warn('fetchCustomerDetail failed:', (e as Error).message);
    if (state.musteriDrawerOpen === slug) {
      const body = $('drawer-body');
      if (body && !state.musteriDetailCache[slug]) {
        body.innerHTML = '<div class="drawer-error">Müşteri detayı yüklenemedi.</div>';
      }
    }
  }
}

async function fetchDecisions(slug: string): Promise<void> {
  try {
    const r = await apiFetch(`/castintech-api/decisions/all?client=${encodeURIComponent(slug)}`);
    if (!r.ok) throw new Error('decisions HTTP ' + r.status);
    const data = (await r.json()) as { decisions?: Array<{ summary?: string; title?: string; date?: string }> };
    // Markdown source: title + summary listesi join.
    const decisions = data.decisions || [];
    const md = decisions.map(d => {
      const head = d.title ? `### ${d.title}` : '';
      const date = d.date ? `*${d.date}*` : '';
      const body = d.summary || '';
      return [head, date, body].filter(Boolean).join('\n\n');
    }).join('\n\n---\n\n');
    state.musteriDrawerDecisions[slug] = { content: md, fetchedAt: Date.now() };
    if (state.musteriDrawerOpen === slug && state.musteriDrawerTab === 'kararlar') {
      renderDrawer(slug);
    }
  } catch (e) {
    console.warn('fetchDecisions failed:', (e as Error).message);
  }
}

// ── Drawer render ───────────────────────────────────────────────────

export function renderDrawer(slug: string): void {
  ensureDrawerDom();
  const titleEl = $('drawer-title');
  const bodyEl = $('drawer-body');
  if (titleEl) titleEl.textContent = slug;

  // Tab active class güncelle.
  const tabsRoot = document.getElementById('drawer-tabs');
  if (tabsRoot) {
    const buttons = tabsRoot.querySelectorAll<HTMLButtonElement>('button[data-tab]');
    buttons.forEach(b => {
      const tab = b.getAttribute('data-tab') as TabId | null;
      b.classList.toggle('active', tab === state.musteriDrawerTab);
    });
  }

  if (!bodyEl) return;

  const cached = state.musteriDetailCache[slug];
  if (!cached) {
    bodyEl.innerHTML = '<div class="drawer-loading">Yükleniyor…</div>';
    return;
  }

  const detail = cached.data;
  const tab = state.musteriDrawerTab;
  bodyEl.innerHTML = renderTabContent(tab, slug, detail);
}

function renderTabContent(tab: TabId, slug: string, d: CustomerDetail): string {
  switch (tab) {
    case 'genel':    return renderGenelTab(d);
    case 'projeler': return renderProjelerTab(d);
    case 'faturalar': return renderFaturalarTab(d);
    case 'kararlar': return renderKararlarTab(slug);
    case 'iletisim': return renderIletisimTab(d);
    case 'aktivite': return renderAktiviteTab(d);
  }
}

function renderGenelTab(d: CustomerDetail): string {
  const lc = d.lifecycle;
  if (!lc) return '<div class="drawer-empty">Lifecycle kaydı yok.</div>';
  const projectCount = d.meta?.project_count ?? d.projects.length;
  const invoiceCount = d.meta?.invoice_count ?? d.invoices.length;

  const history = Array.isArray(lc.history) ? lc.history : [];
  const timeline = history.length === 0
    ? '<div class="drawer-empty">Geçiş geçmişi yok.</div>'
    : `<ul class="lifecycle-history">${
        history.map((h: CustomerHistoryEntry) => `
          <li>
            <span class="hist-state">${escHtml(STATE_LABELS[h.state] || h.state)}</span>
            <span class="hist-owner">@${escHtml(h.owner || '—')}</span>
            <span class="hist-time">${escHtml(formatRelativeTime(h.entered_at))}</span>
            ${h.exit_reason ? `<div class="hist-reason">${escHtml(h.exit_reason)}</div>` : ''}
          </li>
        `).join('')
      }</ul>`;

  return `
    <div class="drawer-section">
      <h3>Lifecycle</h3>
      <dl class="kv">
        <dt>Durum</dt><dd>${escHtml(STATE_LABELS[lc.current_state] || lc.current_state)}</dd>
        <dt>Sahip</dt><dd>@${escHtml(lc.owner_agent || '—')}</dd>
        <dt>Giriş</dt><dd>${escHtml(formatRelativeTime(lc.entered_at))}</dd>
        <dt>Güncelleme</dt><dd>${escHtml(formatRelativeTime(lc.updated_at))}</dd>
        <dt>Proje sayısı</dt><dd>${projectCount}</dd>
        <dt>Fatura sayısı</dt><dd>${invoiceCount}</dd>
      </dl>
    </div>
    <div class="drawer-section">
      <h3>Geçmiş</h3>
      ${timeline}
    </div>
  `;
}

function renderProjelerTab(d: CustomerDetail): string {
  const projects = d.projects || [];
  const grouped: Record<string, CustomerProjectRow[]> = {};
  for (const s of PROJECT_STATES) grouped[s] = [];
  for (const p of projects) {
    if (grouped[p.current_state]) grouped[p.current_state].push(p);
  }

  const cols = PROJECT_STATES.map(s => {
    const items = grouped[s] || [];
    if (items.length === 0) return '';
    const cards = items.map(p => `
      <div class="project-mini-card" data-project-slug="${escAttr(p.project_slug)}">
        <div class="pm-title">${escHtml(p.title)}</div>
        <div class="pm-meta">
          <span class="pm-type">${escHtml(p.project_type)}</span>
          <span class="pm-age">${escHtml(formatRelativeTime(p.entered_at))}</span>
        </div>
        ${p.owner_pm_agent_id ? `<div class="pm-owner">@${escHtml(p.owner_pm_agent_id)}</div>` : ''}
      </div>
    `).join('');
    return `
      <div class="project-mini-col" data-state="${s}">
        <div class="pm-col-header">${escHtml(PROJECT_STATE_LABELS[s] || s)} <span class="count">${items.length}</span></div>
        ${cards}
      </div>
    `;
  }).filter(Boolean).join('');

  const empty = projects.length === 0
    ? '<div class="drawer-empty">Henüz proje yok. Telegram\'dan /proje-ekle ile başlatabilirsin.</div>'
    : '';

  return `
    <div class="drawer-section">
      <div class="proj-toolbar">
        <button type="button" class="btn-newproj" data-action="new-project" disabled
          title="Telegram /proje-ekle ile başlat (Faz 43'te dashboard'dan).">+ Yeni proje</button>
      </div>
      ${empty}
      <div class="project-mini-board">${cols}</div>
    </div>
  `;
}

function renderFaturalarTab(d: CustomerDetail): string {
  const invoices = d.invoices || [];
  if (invoices.length === 0) return '<div class="drawer-empty">Fatura yok.</div>';
  const rows = invoices.map((inv: InvoiceTimelineRow) => `
    <li class="inv-row">
      <span class="inv-num">${escHtml(inv.invoice_number)}</span>
      <span class="inv-amount">${escHtml(formatAmount(inv.total_amount, inv.currency))}</span>
      <span class="inv-status status-${escAttr(inv.status)}">${escHtml(inv.status)}</span>
      <span class="inv-due">${escHtml(inv.due_date || '—')}</span>
    </li>
  `).join('');
  return `<div class="drawer-section"><ul class="invoice-list">${rows}</ul></div>`;
}

function renderKararlarTab(slug: string): string {
  const cached = state.musteriDrawerDecisions[slug];
  const fresh = cached && (Date.now() - cached.fetchedAt) < DETAIL_CACHE_TTL_MS;
  if (!fresh) {
    // Lazy fetch — bir sonraki render'da görünecek.
    fetchDecisions(slug);
    return '<div class="drawer-loading">Kararlar yükleniyor…</div>';
  }
  if (!cached.content) return '<div class="drawer-empty">Karar yok.</div>';
  // renderMd zaten escape eder.
  return `<div class="drawer-section markdown-body">${renderMd(cached.content)}</div>`;
}

function renderIletisimTab(d: CustomerDetail): string {
  const audit = d.recent_audit || [];
  // Filtre: action='handoff' veya source='telegram-session'.
  const filtered = audit.filter((a: AuditRow) => {
    const meta = (a.metadata || {}) as Record<string, unknown>;
    return a.action === 'handoff' || meta.source === 'telegram-session';
  });
  if (filtered.length === 0) {
    return '<div class="drawer-empty">İletişim kaydı yok. (Faz 43\'te genişler.)</div>';
  }
  const items = filtered.map((a: AuditRow) => `
    <li class="comm-row">
      <span class="comm-action">${escHtml(a.action || a.title || '—')}</span>
      <span class="comm-actor">@${escHtml(a.actor || '—')}</span>
      <span class="comm-time">${escHtml(formatRelativeTime(a.ts || a.created_at))}</span>
    </li>
  `).join('');
  return `<div class="drawer-section"><ul class="comm-list">${items}</ul></div>`;
}

function renderAktiviteTab(d: CustomerDetail): string {
  const audit = (d.recent_audit || []).slice(0, 20);
  if (audit.length === 0) return '<div class="drawer-empty">Aktivite yok.</div>';
  const items = audit.map((a: AuditRow) => `
    <li class="act-row">
      <span class="act-title">${escHtml(a.title || '—')}</span>
      <span class="act-action">${escHtml(a.action || '—')}</span>
      <span class="act-actor">@${escHtml(a.actor || '—')}</span>
      <span class="act-time">${escHtml(formatRelativeTime(a.ts || a.created_at))}</span>
    </li>
  `).join('');
  return `<div class="drawer-section"><ul class="activity-list">${items}</ul></div>`;
}

// ── SSE invalidation API ────────────────────────────────────────────
// sse.ts project.created/transition + lifecycle.transition event'lerinden
// çağrılır. Drawer açıksa ve aynı client_slug etkileniyorsa re-fetch.

export function invalidateOpenDrawer(clientSlug?: string): void {
  if (!state.musteriDrawerOpen) return;
  if (clientSlug && clientSlug !== state.musteriDrawerOpen) return;
  // Cache invalidate + re-fetch.
  delete state.musteriDetailCache[state.musteriDrawerOpen];
  fetchCustomerDetail(state.musteriDrawerOpen);
}
